import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { APIError, api, jsonBody } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { BrandMark } from "../../brand/BrandMark";
import { formatDate, formatDateTime, formatMoney, titleCase } from "../../lib/format";
import type { TenantBrand } from "../../theme/tenantBrand";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  StatusPill,
} from "../../ui/components";
import {
  BoundarySkeleton,
  SkeletonCard,
  SkeletonPageHeader,
  SkeletonTable,
} from "../../ui/skeletons";

interface PortalAccess {
  relationshipId: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  brand: TenantBrand;
  customerId: string;
  customerName: string;
  customerOrganisation: string;
}
interface PortalInvoice {
  id: string;
  number: string;
  status: string;
  currency: string;
  totalMinor: number;
  balanceMinor: number;
  issuedAt?: string;
  dueAt?: string;
  paymentUrl?: string;
}
interface PortalDocument {
  id: string;
  type: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}
interface PortalBooking {
  id: string;
  reference: string;
  title: string;
  status: string;
  start: string;
  end: string;
  timezone: string;
  attendeeCount?: number;
  customerNotes: string;
  locations: Array<{ venueName: string; locationName: string }>;
  invoices: PortalInvoice[];
  documents: PortalDocument[];
}
interface PortalAction {
  id: string;
  customerId: string;
  bookingId: string;
  type: string;
  payload: Record<string, unknown>;
  status: string;
  reviewReason?: string;
  createdAt: string;
  updatedAt: string;
}
interface Preference {
  purpose: string;
  channel: string;
  state: string;
  lawfulBasis: string;
  source: string;
  recordedAt: string;
}
interface Dashboard {
  access: PortalAccess;
  bookings: PortalBooking[];
  actions: PortalAction[];
  preferences: Preference[];
}

function hasItems<T>(items: T[]): items is [T, ...T[]] {
  return items.length > 0;
}

function PortalAccessHelp({
  email,
  error,
  switching,
  onSwitch,
}: {
  email: string;
  error?: unknown;
  switching: boolean;
  onSwitch(): Promise<void>;
}) {
  return (
    <main className="public-main">
      <EmptyState
        title="Use the customer account for this portal"
        description={`You’re signed in as ${email}, but this account is not linked to a customer record for this venue. Switch accounts and sign in with the email that received the portal invitation.`}
        action={
          <button
            className="button primary"
            type="button"
            disabled={switching}
            onClick={() => void onSwitch()}
          >
            {switching ? "Signing out…" : "Switch account"}
          </button>
        }
      />
      {error ? <ErrorPanel error={error} /> : null}
    </main>
  );
}

export function CustomerPortalPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { tenantSlug = "" } = useParams();
  const access = useQuery({
    queryKey: ["portal", tenantSlug, "access"],
    queryFn: () =>
      api<{ items: PortalAccess[] }>(`/api/v1/portal/${encodeURIComponent(tenantSlug)}/access`),
    enabled: Boolean(auth.session),
  });
  const [customerId, setCustomerId] = useState("");
  const selectedCustomer = customerId || access.data?.items[0]?.customerId || "";
  const dashboard = useQuery({
    queryKey: ["portal", tenantSlug, selectedCustomer, "dashboard"],
    queryFn: () =>
      api<Dashboard>(
        `/api/v1/portal/${encodeURIComponent(tenantSlug)}/customers/${selectedCustomer}/dashboard`,
      ),
    enabled: Boolean(auth.session && selectedCustomer),
  });

  const [actionType, setActionType] = useState("message");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function switchAccount() {
    setWorking(true);
    setError(undefined);
    try {
      await auth.signOut();
      navigate("/login", { replace: true, state: { from: `/portal/${tenantSlug}` } });
    } catch (caught) {
      setError(caught);
      setWorking(false);
    }
  }

  async function sendAction(event: FormEvent<HTMLFormElement>, booking: PortalBooking) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api(
        `/api/v1/portal/${encodeURIComponent(tenantSlug)}/customers/${selectedCustomer}/bookings/${booking.id}/actions`,
        {
          method: "POST",
          body: jsonBody({
            type: actionType,
            message: String(form.get("message")),
            choiceKey: String(form.get("choiceKey")),
            choiceValue: String(form.get("choiceValue")),
            proposedStart: form.get("proposedStart")
              ? new Date(String(form.get("proposedStart"))).toISOString()
              : null,
            proposedEnd: form.get("proposedEnd")
              ? new Date(String(form.get("proposedEnd"))).toISOString()
              : null,
          }),
        },
      );
      formElement.reset();
      await dashboard.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updatePreference(marketingEmail: boolean) {
    setWorking(true);
    setError(undefined);
    try {
      await api(
        `/api/v1/portal/${encodeURIComponent(tenantSlug)}/customers/${selectedCustomer}/preferences`,
        { method: "PUT", body: jsonBody({ marketingEmail }) },
      );
      await dashboard.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (auth.loading) return <BoundarySkeleton />;
  if (!auth.session)
    return <Navigate to="/login" replace state={{ from: `/portal/${tenantSlug}` }} />;
  if (access.isLoading || dashboard.isLoading)
    return (
      <div className="public-layout">
        <main className="public-main">
          <SkeletonPageHeader />
          <SkeletonCard />
          <SkeletonTable />
        </main>
      </div>
    );
  const accessDenied = access.error instanceof APIError && access.error.status === 403;
  if (accessDenied)
    return (
      <PortalAccessHelp
        email={auth.session.user.email}
        error={error}
        switching={working}
        onSwitch={switchAccount}
      />
    );
  if (access.error || dashboard.error)
    return (
      <main className="public-main">
        <ErrorPanel error={access.error ?? dashboard.error} />
      </main>
    );
  if (!access.data || !hasItems(access.data.items))
    return (
      <PortalAccessHelp
        email={auth.session.user.email}
        error={error}
        switching={working}
        onSwitch={switchAccount}
      />
    );
  const data = dashboard.data;
  const brand = data?.access.brand ?? access.data.items[0].brand;
  const marketing =
    data?.preferences.find((item) => item.purpose === "marketing" && item.channel === "email")
      ?.state === "granted";

  return (
    <div className="public-layout">
      <header className="public-header">
        {brand?.logoUrl ? <img className="brand-logo" src={brand.logoUrl} alt="" /> : <BrandMark />}
        <div>
          <strong>{data?.access.tenantName ?? access.data.items[0].tenantName}</strong>
          <small>Customer portal</small>
        </div>
        <div className="page-actions">
          {access.data.items.length > 1 ? (
            <select
              aria-label="Customer account"
              value={selectedCustomer}
              onChange={(event) => setCustomerId(event.target.value)}
            >
              {access.data.items.map((item) => (
                <option key={item.customerId} value={item.customerId}>
                  {item.customerOrganisation
                    ? `${item.customerName} (${item.customerOrganisation})`
                    : item.customerName}
                </option>
              ))}
            </select>
          ) : null}
          <button
            className="button secondary small"
            type="button"
            disabled={working}
            onClick={() => void switchAccount()}
          >
            {working ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </header>
      <main className="public-main">
        <header className="page-header">
          <div>
            <h1>{data?.access.customerName}</h1>
            {data?.access.customerOrganisation ? (
              <p className="muted">{data.access.customerOrganisation}</p>
            ) : null}
            <p>
              Everything for your bookings in one place: dates, invoices, documents, and messages.
            </p>
          </div>
        </header>
        {error ? <ErrorPanel error={error} /> : null}
        <Card>
          <CardHeader
            title="Email preferences"
            description="We’ll always send important booking and security emails. You can choose whether to receive news and offers."
          />
          <div className="split">
            <span>Marketing emails</span>
            <button
              className="button secondary small"
              type="button"
              disabled={working}
              onClick={() => updatePreference(!marketing)}
            >
              {marketing ? "Turn off marketing emails" : "Receive marketing emails"}
            </button>
          </div>
        </Card>
        {data?.bookings.length ? (
          <div className="stack">
            {data.bookings.map((booking) => (
              <Card key={booking.id}>
                <CardHeader
                  title={`${booking.reference} · ${booking.title}`}
                  description={`${formatDateTime(booking.start, booking.timezone)} to ${formatDateTime(booking.end, booking.timezone)}`}
                  actions={<StatusPill value={booking.status} />}
                />
                <dl className="definition-list">
                  <dt>Locations</dt>
                  <dd>
                    {booking.locations
                      .map((location) => `${location.venueName} / ${location.locationName}`)
                      .join(", ")}
                  </dd>
                  <dt>Attendees</dt>
                  <dd>{booking.attendeeCount ?? "Not specified"}</dd>
                  <dt>Notes from the venue team</dt>
                  <dd>{booking.customerNotes || "No notes have been shared yet"}</dd>
                </dl>
                <div className="content-grid">
                  <section className="span-6">
                    <h3>Invoices</h3>
                    {booking.invoices.length ? (
                      <DataTable
                        caption={`Invoices for ${booking.reference}`}
                        items={booking.invoices}
                        getKey={(item) => item.id}
                        columns={[
                          { label: "Number", render: (item) => item.number },
                          {
                            label: "Balance",
                            render: (item) => formatMoney(item.balanceMinor, item.currency),
                          },
                          { label: "Due", render: (item) => formatDate(item.dueAt) },
                          { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                          {
                            label: "Actions",
                            render: (item) => (
                              <div className="inline">
                                <a
                                  className="button secondary small"
                                  href={`/api/v1/portal/${encodeURIComponent(tenantSlug)}/customers/${selectedCustomer}/invoices/${item.id}/pdf`}
                                  download
                                >
                                  Download PDF
                                </a>
                                {item.paymentUrl ? (
                                  <a className="button primary small" href={item.paymentUrl}>
                                    Pay balance
                                  </a>
                                ) : null}
                              </div>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <p className="muted">No invoices shared.</p>
                    )}
                  </section>
                  <section className="span-6">
                    <h3>Documents</h3>
                    {booking.documents.length ? (
                      <ul>
                        {booking.documents.map((document) => (
                          <li key={document.id}>
                            <a
                              href={`/api/v1/portal/${encodeURIComponent(tenantSlug)}/customers/${selectedCustomer}/bookings/${booking.id}/documents/${document.id}/download`}
                              download
                            >
                              {document.filename}
                            </a>{" "}
                            <small>({Math.ceil(document.sizeBytes / 1024)} KB)</small>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="muted">No documents shared.</p>
                    )}
                  </section>
                </div>
                <form className="form-grid" onSubmit={(event) => sendAction(event, booking)}>
                  <Field label="What would you like to do?">
                    <select
                      value={actionType}
                      onChange={(event) => setActionType(event.target.value)}
                    >
                      <option value="message">Message venue team</option>
                      <option value="cancellation_request">Request cancellation</option>
                      <option value="change_request">Request date/time change</option>
                      <option value="choice_response">Respond to a choice</option>
                    </select>
                  </Field>
                  {actionType === "choice_response" ? (
                    <>
                      <Field label="Question or option name">
                        <input name="choiceKey" required />
                      </Field>
                      <Field label="Your answer">
                        <input name="choiceValue" required />
                      </Field>
                    </>
                  ) : (
                    <Field label="Message">
                      <textarea name="message" required minLength={3} />
                    </Field>
                  )}
                  {actionType === "change_request" ? (
                    <>
                      <Field label="Preferred start">
                        <input name="proposedStart" type="datetime-local" required />
                      </Field>
                      <Field label="Preferred end">
                        <input name="proposedEnd" type="datetime-local" required />
                      </Field>
                    </>
                  ) : null}
                  <div className="full">
                    <FormActions submitting={working} submitLabel="Send to venue team" />
                  </div>
                </form>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No bookings"
            description="Bookings shared with this customer will appear here."
          />
        )}
        {data?.actions.length ? (
          <Card>
            <CardHeader title="Messages and requests" />
            <DataTable
              caption="Messages and requests"
              items={data.actions}
              getKey={(item) => item.id}
              columns={[
                { label: "Type", render: (item) => titleCase(item.type) },
                { label: "Sent", render: (item) => formatDate(item.createdAt) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                {
                  label: "Venue team response",
                  render: (item) => item.reviewReason || "Waiting for the venue team",
                },
              ]}
            />
          </Card>
        ) : null}
      </main>
      <footer className="public-footer">
        <small>
          Your account only shows bookings and documents shared with you. The venue decides how your
          details are used; the platform's own <Link to="/legal/privacy">privacy notice</Link>{" "}
          explains its part.
        </small>
      </footer>
    </div>
  );
}
